import { InfoCircleOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { TOUR_DEMO_LEAD_RECORD } from "../content/tour-demo-data.ts"
import { DemoLeadDocumentsSection } from "./demo-sections/DemoLeadDocumentsSection.tsx"
import { DemoLeadOverviewSection } from "./demo-sections/DemoLeadOverviewSection.tsx"
import { DemoLeadScopeSection } from "./demo-sections/DemoLeadScopeSection.tsx"

const { Text } = Typography

type LeadPreviewTab = "overview" | "scope" | "documents"

const TABS: { key: LeadPreviewTab; labelKey: string }[] = [
  { key: "overview", labelKey: "guide.productTour.context.leadOverview" },
  { key: "scope", labelKey: "guide.productTour.context.leadScope" },
  { key: "documents", labelKey: "guide.productTour.context.leadDocuments" }
]

/**
 * The REAL Lead workspace tabs (`LeadOverviewTab`/`LeadScopeTab`/
 * `LeadDocumentsTab`, via the `Demo*Section` wrappers in `demo-sections/`)
 * fed a hardcoded "Kitchen Remodel" Lead — never fetched, never saved — the
 * Product Tour routes here instead of a real `/leads/:id` whenever the
 * account has no real Lead yet (see `product-tour-steps.ts`'s
 * `resolveRoute` fallbacks). Same `data-guide-target` names as the real Lead
 * workspace's own tabs (each tab component sets its own), so the tour
 * engine highlights this exactly like it would the real page.
 */
export const GuideTourPreviewLeadPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const activeTab = (searchParams.get("tab") as LeadPreviewTab) || "overview"

  useEffect(() => {
    generateDocumentTitle(TOUR_DEMO_LEAD_RECORD.lead_name ?? "Kitchen Remodel")
  }, [])

  return (
    <Flex vertical gap={16} className="pb-10">
      <Flex align="center" gap={8}>
        <Tag color="gold" icon={<InfoCircleOutlined />}>
          {t("guide.tourDemo.exampleLeadBadge")}
        </Tag>
        <Text type="secondary" className="text-xs">
          {t("guide.tourDemo.exampleNotice")}
        </Text>
      </Flex>

      <Flex gap={16}>
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            to={`${RoutesPaths.guide.tourPreview.lead}?tab=${tab.key}`}
            className={
              tab.key === activeTab
                ? "text-sm font-semibold text-primary"
                : "text-sm text-gray-500"
            }
          >
            {t(tab.labelKey)}
          </Link>
        ))}
      </Flex>

      {activeTab === "overview" && <DemoLeadOverviewSection />}
      {activeTab === "scope" && <DemoLeadScopeSection />}
      {activeTab === "documents" && <DemoLeadDocumentsSection />}
    </Flex>
  )
}
